import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Clock } from 'lucide-react';
import { formatDateBrussels } from '@/lib/utils';
import { formatHmInBrussels } from '@/lib/brusselsSchedule';
import { formatHmForDisplayFr, normalizeEndTimeToHm } from '@/lib/eventTimes';
import AddToCalendar from './AddToCalendar';

export default function EventCard({ event, showImage = true }) {
    const typeColors = {
        'Conférence': 'bg-primary-100 text-primary-800',
        'Excursion': 'bg-green-100 text-green-800',
        'Goûter': 'bg-accent-100 text-accent-800',
    };

    const getFallbackImage = (type) => {
        switch (type) {
            case 'Conférence': return '/images/Fallback image conférence.png';
            case 'Excursion': return '/images/bus-maastricht.jpg';
            case 'Goûter': return 'https://images.unsplash.com/photo-1559563362-c667ba5f5480?q=80&w=800&auto=format&fit=crop'; // Coffee/Tea time
            default: return '/images/Fallback image conférence.png';
        }
    };

    const getEventImage = (evt) => {
        // Force la photo locale demandée pour l'excursion de Maastricht.
        if (evt?.slug === 'excursion-maastricht-2026') {
            return '/images/excursion-maastricht-2026.webp';
        }
        return evt?.image_url || getFallbackImage(evt?.type);
    };

    const [imgSrc, setImgSrc] = useState(getEventImage(event));

    useEffect(() => {
        setImgSrc(getEventImage(event));
    }, [event.image_url, event.type, event.slug]);

    const handleImageError = () => {
        setImgSrc(getFallbackImage(event.type));
    };

    const detailPath = event.slug ? `/agenda/${event.slug}` : '/agenda';

    return (
        <motion.div
            whileHover={{ y: -5 }}
            transition={{ type: 'spring', stiffness: 300 }}
            className="group bg-white rounded-xl shadow-sm hover:shadow-xl border border-gray-100 flex flex-col h-full relative"
        >
            <Link
                to={detailPath}
                className="absolute inset-0 z-[1] rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
                aria-label={`Voir l’événement : ${event.title}`}
            />
            <div className="relative z-[2] flex flex-col h-full pointer-events-none">
                {showImage && (
                    <div className="relative h-52 overflow-hidden rounded-t-xl">
                        <img
                            src={imgSrc}
                            alt=""
                            onError={handleImageError}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                            aria-hidden
                        />
                        <div className="absolute top-4 left-4">
                            <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${typeColors[event.type] || 'bg-gray-100 text-gray-800'}`}>
                                {event.type}
                            </span>
                        </div>
                    </div>
                )}

                {!showImage && (
                    <div className="pt-6 px-6">
                        <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${typeColors[event.type] || 'bg-gray-100 text-gray-800'}`}>
                            {event.type}
                        </span>
                    </div>
                )}

                <div className="p-6 flex-1 flex flex-col">
                    <div className="text-sm text-gray-500 font-medium mb-2 space-y-1.5">
                        <div>{formatDateBrussels(event.date)}</div>
                        <div className="flex items-start gap-1.5 text-gray-600">
                            <Clock size={14} className="text-primary-400 shrink-0 mt-0.5" />
                            <div className="flex flex-col gap-0.5">
                                <span>Début {formatHmForDisplayFr(formatHmInBrussels(event.date))}</span>
                                <span>Fin {formatHmForDisplayFr(normalizeEndTimeToHm(event.end_time))}</span>
                            </div>
                        </div>
                    </div>

                    <h3 className="text-xl font-bold text-primary-900 mb-3 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {event.title}
                    </h3>

                    {event.description && (
                        <p className="text-gray-600 mb-4 line-clamp-2 text-sm flex-1">
                            {event.description}
                        </p>
                    )}

                    <div className="mt-auto space-y-4">
                        <div className="flex items-center gap-2 text-gray-500 text-sm">
                            <MapPin size={16} className="text-primary-400" />
                            <span className="truncate">{event.location}</span>
                        </div>

                        <div className="pt-4 border-t border-gray-100 flex items-center justify-between gap-3">
                            <span className="text-primary-600 font-medium text-sm">
                                Voir les détails
                            </span>
                            <div className="pointer-events-auto shrink-0">
                                <AddToCalendar event={event} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </motion.div>
    );
}
